import { Fragment, JSX } from 'react'
import { OverviewItem, SalaryDetails } from '@/modules/salary/types'
import { EditReviewLink } from '@/modules/salary/details/edit-review-link'
import {
  OnboardingAccountType,
  OnboardingPageIds,
} from '@/modules/surveys/configs/onboarding/constants'
import { DetailNote } from '@/modules/salary/details/detail-note'
import { usePostHog } from 'posthog-js/react'
import { useProfessionField } from '@/modules/onboarding/use-profession-field'
import { AcademicBadge } from '../common/accademic-badge'
import { RatingInput } from '@/components/ui/rating-input'
import {
  LOCUM_TENENS_VALUE,
  PRN_VALUE,
} from '@/modules/surveys/configs/onboarding/current-job-pane-config'

const WORK_OVERVIEW_TITLE = 'Workload'
const JOB_OVERVIEW_TITLE = 'Job Overview'
const BENEFITS_TITLE = 'Benefits'
const POSTHOG_TITLES = {
  [WORK_OVERVIEW_TITLE.toLowerCase()]: 'schedule',
  [BENEFITS_TITLE.toLowerCase()]: 'benefits',
}

interface WorkOverviewProps {
  salaryDetails?: SalaryDetails
  isOnboardingReviewPane?: boolean
  accountType?: OnboardingAccountType
}

function SalaryDetailsSection({
  items,
  title,
  onboardingPageId,
  isOnboardingReviewPane,
  accountType,
  children,
}: {
  items: OverviewItem[]
  title: string
  onboardingPageId: OnboardingPageIds
  isOnboardingReviewPane?: boolean
  accountType?: OnboardingAccountType
  children?: React.ReactNode
}) {
  const posthog = usePostHog()
  const { professionField } = useProfessionField()

  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-copy-primary font-semibold mb-2">{title}</h3>
        {isOnboardingReviewPane ? (
          <EditReviewLink
            pageId={onboardingPageId}
            onClick={() => {
              const postHogTitle = POSTHOG_TITLES[title.toLowerCase()]
              posthog.capture(`clicked_${postHogTitle}_details_edit`, {
                screen_name: 'review_salary',
                product_area: 'onboarding',
                module_name: 'main',
                profession_type: accountType,
                profession: professionField,
              })
            }}
          />
        ) : null}
      </div>
      <div className="grid grid-cols-[max-content_1fr] gap-x-2 text-sm gap-y-2">
        {items.map(
          (
            { icon: Icon, subText, text, list, type, value }: OverviewItem,
            index,
          ) => (
            <Fragment key={index}>
              <div className="flex items-start min-w-40">
                {Icon ? (
                  <Icon className="w-5 h-5 mr-3 text-topNav-unselected" />
                ) : null}
                <span className="text-copy-primary font-medium mr-1">
                  {text}
                </span>
              </div>
              {subText ? (
                <span className="text-copy-muted">
                  {Array.isArray(subText) ? subText.join(', ') : subText}
                </span>
              ) : null}
              {list ? (
                <ul className="text-copy-muted">
                  {list.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ) : null}
              {type === 'rating' ? (
                <RatingInput value={value} disabled className="ml-0" />
              ) : null}
            </Fragment>
          ),
        )}
      </div>
      {children}
    </div>
  )
}

export function OverviewBenefits({
  salaryDetails,
  isOnboardingReviewPane,
  accountType,
}: WorkOverviewProps): JSX.Element {
  const workItems = salaryDetails?.workOverview || []
  const benefits = salaryDetails?.benefits || []
  const jobItems = salaryDetails?.jobOverview || []
  const isLocumsOrPrn =
    salaryDetails?.jobType === LOCUM_TENENS_VALUE ||
    salaryDetails?.jobType === PRN_VALUE
  const showAgencySection =
    salaryDetails?.agencyInformation &&
    salaryDetails.agencyInformation.length > 0 &&
    salaryDetails.jobType !== PRN_VALUE

  /**
   * Since academic affiliation is a badge we added it to employer item
   * in case is needed.
   */
  if (salaryDetails?.academicAffiliation) {
    const employerItem = jobItems.find((i) => i.text === 'Employer')
    if (employerItem && typeof employerItem.subText === 'string') {
      employerItem.subText = (
        <>
          <p className="mb-0.5">{employerItem.subText}</p>
          <AcademicBadge
            academicAffiliation={salaryDetails.academicAffiliation}
            className="ml-0"
          />
        </>
      )
    }
  }

  return (
    <>
      {jobItems?.length > 0 ? (
        <>
          <SalaryDetailsSection
            items={jobItems}
            title={JOB_OVERVIEW_TITLE}
            onboardingPageId={OnboardingPageIds.CurrentJob}
            isOnboardingReviewPane={isOnboardingReviewPane}
            accountType={accountType}
          />
          <div className="w-full h-px bg-background-muted" />
        </>
      ) : null}
      {workItems?.length > 0 ? (
        <SalaryDetailsSection
          items={workItems}
          title={WORK_OVERVIEW_TITLE}
          onboardingPageId={OnboardingPageIds.Responsibilities}
          isOnboardingReviewPane={isOnboardingReviewPane}
          accountType={accountType}
        />
      ) : null}
      {salaryDetails?.additionalDetails ? (
        <DetailNote title="Additional Details" className="my-4">
          {salaryDetails.additionalDetails}
        </DetailNote>
      ) : null}
      {(workItems.length > 0 || salaryDetails?.additionalDetails) &&
      showAgencySection ? (
        <div className="w-full h-px bg-background-muted" />
      ) : null}
      {showAgencySection ? (
        <SalaryDetailsSection
          items={salaryDetails.agencyInformation}
          title="Agency"
          onboardingPageId={OnboardingPageIds.LocumPrnWrapUp}
          isOnboardingReviewPane={isOnboardingReviewPane}
          accountType={accountType}
        >
          {salaryDetails?.locums?.agencyFeedback ? (
            <DetailNote title="Review" className="my-4 pl-0">
              {salaryDetails.locums?.agencyFeedback}
            </DetailNote>
          ) : null}
        </SalaryDetailsSection>
      ) : null}
      <div className="w-full h-px bg-background-muted" />
      {benefits?.length > 0 && !isLocumsOrPrn ? (
        <SalaryDetailsSection
          items={benefits}
          title={BENEFITS_TITLE}
          onboardingPageId={OnboardingPageIds.Benefits}
          isOnboardingReviewPane={isOnboardingReviewPane}
          accountType={accountType}
        />
      ) : null}
    </>
  )
}
